import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Container,
  Paper,
  CircularProgress,
  Divider,
  Alert,
  Grid,
  InputAdornment,
  IconButton,
  Stack,
  Chip
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import {
  invitationService,
  UserInvitation,
  UserInvitationAcceptRequest
} from 'src/services/invitationService';
import { format } from 'date-fns';
import Logo from 'src/components/LogoSign';
import { useTranslation } from 'react-i18next';

function InvitationAcceptance() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get('token');

  const [invitation, setInvitation] = useState<UserInvitation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Form fields
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Form validation
  const [nameError, setNameError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [confirmPasswordError, setConfirmPasswordError] = useState<
    string | null
  >(null);
  const [phoneNumberError, setPhoneNumberError] = useState<string | null>(null);

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError(t('invitation.invalidLink'));
        setLoading(false);
        return;
      }

      try {
        const invitationData = await invitationService.validateInvitation(
          token
        );
        setInvitation(invitationData);
      } catch (err) {
        console.error('Failed to validate invitation token:', err);
        setError(t('invitation.invalidOrExpired'));
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, [token, t]);

  const validateName = (): boolean => {
    if (!name.trim()) {
      setNameError(t('invitation.errors.nameRequired'));
      return false;
    }
    setNameError(null);
    return true;
  };

  const validatePassword = (): boolean => {
    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    if (!password) {
      setPasswordError(t('invitation.errors.passwordRequired'));
      return false;
    }
    if (!passwordRegex.test(password)) {
      setPasswordError(t('invitation.errors.passwordStrength'));
      return false;
    }
    setPasswordError(null);
    return true;
  };

  const validateConfirmPassword = (): boolean => {
    if (password !== confirmPassword) {
      setConfirmPasswordError(t('invitation.errors.passwordsDoNotMatch'));
      return false;
    }
    setConfirmPasswordError(null);
    return true;
  };

  const validatePhoneNumber = (): boolean => {
    if (phoneNumber && !/^\+?[0-9\s\-()]{5,20}$/.test(phoneNumber)) {
      setPhoneNumberError(t('invitation.errors.invalidPhone'));
      return false;
    }
    setPhoneNumberError(null);
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const isNameValid = validateName();
    const isPasswordValid = validatePassword();
    const isConfirmPasswordValid = validateConfirmPassword();
    const isPhoneNumberValid = validatePhoneNumber();
    if (
      !isNameValid ||
      !isPasswordValid ||
      !isConfirmPasswordValid ||
      !isPhoneNumberValid
    ) {
      return;
    }
    if (!token) {
      setError(t('invitation.errors.invalidToken'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const acceptData: UserInvitationAcceptRequest = {
        token,
        name,
        password,
        phoneNumber: phoneNumber || undefined
      };
      await invitationService.acceptInvitation(acceptData);
      // Redirect to login page with success message
      navigate('/login?registered=true');
    } catch (err: any) {
      console.error('Failed to accept invitation:', err);
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError(t('invitation.errors.failedToCreate'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const isInvitationValid = invitation && invitation.status === 'PENDING';
  const isInvitationExpired =
    invitation &&
    (invitation.status === 'EXPIRED' ||
      new Date(invitation.expiryDate) < new Date());

  return (
    <>
      <Helmet>
        <title>{t('invitation.pageTitle')}</title>
      </Helmet>
      <Box
        display="flex"
        flexDirection="column"
        minHeight="100vh"
        alignItems="center"
        justifyContent="center"
        sx={{
          py: 5,
          backgroundColor: (theme) => theme.palette.background.default
        }}
      >
        <Container maxWidth="sm">
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            mb={3}
          >
            <Logo />
            <Typography
              variant="h3"
              component="h1"
              gutterBottom
              align="center"
              sx={{ ml: 2 }}
            >
              {t('invitation.heading')}
            </Typography>
          </Box>
          <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
            <Typography variant="h4" component="h2" gutterBottom align="center">
              {t('invitation.acceptTitle')}
            </Typography>
            {loading ? (
              <Box display="flex" justifyContent="center" my={4}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ my: 2 }}>
                {error}
              </Alert>
            ) : !isInvitationValid ? (
              <Box>
                <Alert
                  severity={isInvitationExpired ? 'warning' : 'error'}
                  sx={{ my: 2 }}
                >
                  {isInvitationExpired
                    ? t('invitation.expired')
                    : t('invitation.invalid')}
                </Alert>
                <Box textAlign="center" mt={3}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => navigate('/login')}
                  >
                    {t('invitation.goToLogin')}
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box>
                <Box my={3}>
                  <Stack direction="row" spacing={2} alignItems="center" mb={1}>
                    <Typography variant="subtitle1">
                      {t('invitation.invitedTo')}
                    </Typography>
                    <Typography variant="h6" color="primary">
                      {invitation.companyName}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={2} alignItems="center" mb={1}>
                    <Typography variant="body2" color="textSecondary">
                      {t('invitation.invitedAs')}
                    </Typography>
                    <Chip
                      label={invitation.roleId}
                      color="primary"
                      size="small"
                      variant="outlined"
                    />
                  </Stack>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Typography variant="body2" color="textSecondary">
                      {t('invitation.expires')}
                    </Typography>
                    <Typography variant="body2">
                      {format(
                        new Date(invitation.expiryDate),
                        'MMM dd, yyyy HH:mm'
                      )}
                    </Typography>
                  </Stack>
                </Box>
                <Divider sx={{ my: 3 }} />
                <Typography variant="h6" gutterBottom>
                  {t('invitation.completeRegistration')}
                </Typography>
                <Box component="form" onSubmit={handleSubmit} noValidate>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        required
                        fullWidth
                        id="email"
                        label={t('invitation.email')}
                        value={invitation.email}
                        disabled
                        margin="normal"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        required
                        fullWidth
                        id="name"
                        label={t('invitation.fullName')}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        onBlur={validateName}
                        error={!!nameError}
                        helperText={nameError}
                        margin="normal"
                        disabled={submitting}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        required
                        fullWidth
                        id="password"
                        label={t('invitation.password')}
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onBlur={validatePassword}
                        error={!!passwordError}
                        helperText={passwordError}
                        margin="normal"
                        disabled={submitting}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowPassword(!showPassword)}
                                edge="end"
                              >
                                {showPassword ? (
                                  <VisibilityOff />
                                ) : (
                                  <Visibility />
                                )}
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        required
                        fullWidth
                        id="confirmPassword"
                        label={t('invitation.confirmPassword')}
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        onBlur={validateConfirmPassword}
                        error={!!confirmPasswordError}
                        helperText={confirmPasswordError}
                        margin="normal"
                        disabled={submitting}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() =>
                                  setShowConfirmPassword(!showConfirmPassword)
                                }
                                edge="end"
                              >
                                {showConfirmPassword ? (
                                  <VisibilityOff />
                                ) : (
                                  <Visibility />
                                )}
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        id="phoneNumber"
                        label={t('invitation.phoneOptional')}
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        onBlur={validatePhoneNumber}
                        error={!!phoneNumberError}
                        helperText={phoneNumberError}
                        margin="normal"
                        disabled={submitting}
                      />
                    </Grid>
                  </Grid>
                  <Box mt={3} display="flex" justifyContent="space-between">
                    <Button
                      variant="outlined"
                      onClick={() => navigate('/login')}
                      disabled={submitting}
                    >
                      {t('common.cancel')}
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={submitting}
                      startIcon={
                        submitting ? <CircularProgress size={20} /> : null
                      }
                    >
                      {t('invitation.createAccount')}
                    </Button>
                  </Box>
                </Box>
              </Box>
            )}
          </Paper>
        </Container>
      </Box>
    </>
  );
}

export default InvitationAcceptance;
